package com.iregained.site.components.sections.patients.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.corporate.styles.ActionButtonStyle
import com.iregained.site.components.sections.corporate.styles.ProcurementCardStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Card Unfold
@Composable
private fun Modifier.cardUnfold(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scaleY(if (isVisible) 1f else 0.8f)
        .styleModifier { property("transform-origin", "bottom center") }.transition(
            Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun PatientsPricingOptionsSection(data: PatientsPricingOptionsData = defaultPatientsPricingData) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "patients-pricing-options"
    var isVisible by remember { mutableStateOf(false) }

    if (data.options.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.brand.primarySoft)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.headline.isNotBlank()) {
                        SectionHeader(
                            text = data.headline,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 16.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s)),
                            alignment = Alignment.CenterHorizontally
                        )
                    }
                    if (data.subtitle.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(18.px)
                                .fontWeight(FontWeight.Medium).color(palette.custom.darkText)
                                .textAlign(TextAlign.Center).margin(bottom = 40.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s, delay = 200.ms))
                                .toAttrs { ref { htmlElement -> htmlElement.innerHTML = data.subtitle; onDispose {} } })
                    }
                    SimpleGrid(
                        numColumns = numColumns(base = 1, lg = 3),
                        modifier = Modifier.fillMaxWidth().maxWidth(1440.px).gap(30.px)
                    ) {
                        data.options.forEachIndexed { index, option ->
                            key(option.title) {
                                PatientPricingCard(option, Modifier.cardUnfold(isVisible, 300 + (index * 150)))
                            }
                        }
                    }
                    if (data.partnerBannerText.isNotBlank()){
                        Div(
                            attrs = Modifier.margin(top = 30.px).fontSize(14.px).color(palette.custom.darkText)
                                .opacity(80.percent).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s, delay = 800.ms)).toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.partnerBannerText
                                        onDispose {}
                                    }
                                }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun PatientPricingCard(option: PatientPricingOption, modifier: Modifier) {
    val palette = ColorMode.current.toSitePalette()
    val cardModifier = ProcurementCardStyle.toModifier().then(modifier).fillMaxWidth().borderRadius(12.px)
        .backgroundColor(Colors.White)
        .boxShadow(0.px, 8.px, 24.px, color = palette.custom.darkText.toRgb().copyf(alpha = 0.06f))
        .thenIf(option.isFeatured, Modifier.border(2.px, LineStyle.Solid, palette.brand.primary))
    Box(modifier = cardModifier) {
        Column(
            modifier = Modifier.fillMaxSize().padding(32.px),
            verticalArrangement = Arrangement.SpaceBetween,
            horizontalAlignment = Alignment.Start
        ) {
            Column(modifier = Modifier.fillMaxWidth()) {
                if (option.badge.isNotBlank()) Box(
                    modifier = Modifier.margin(bottom = 16.px).borderRadius(16.px).backgroundColor(
                        if (option.isFeatured) palette.brand.primarySoft else palette.custom.darkText.toRgb()
                            .copyf(alpha = 0.1f)
                    ).padding(topBottom = 4.px, leftRight = 14.px)
                ) {
                    SpanText(
                        text = option.badge,
                        modifier = Modifier.color(if (option.isFeatured) palette.brand.primary else palette.custom.darkText)
                            .fontSize(13.px).fontWeight(FontWeight.Bold)
                    )
                }

                Div(
                    attrs = Modifier.color(palette.brand.primary).fontWeight(FontWeight.Bold).fontSize(25.px)
                        .margin(bottom = 12.px).toAttrs {
                            ref { htmlElement ->
                                htmlElement.innerHTML = option.title
                                onDispose {}
                            }
                        }
                )

                Div(
                    attrs = Modifier.color(palette.custom.darkText).fontSize(18.px).lineHeight(1.4)
                        .fontWeight(FontWeight.Medium).margin(bottom = 16.px).toAttrs {
                            ref { htmlElement ->
                                htmlElement.innerHTML = option.targetProfile
                                onDispose {}
                            }
                        }
                )

                Box(
                    modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(palette.custom.divider)
                        .margin(bottom = 16.px)
                )

                Div(
                    attrs = Modifier.color(palette.custom.darkText).fontSize(18.px).lineHeight(1.5)
                        .margin(bottom = 20.px).toAttrs { ref { el -> el.innerHTML = option.advantage; onDispose {} } })
                Column(modifier = Modifier.gap(12.px).margin(bottom = 24.px)) {
                    option.inclusions.forEach { itemHtml ->
                        key(itemHtml) {
                            Row(verticalAlignment = Alignment.Top, modifier = Modifier.fillMaxWidth().gap(10.px)) {
                                SpanText(
                                    text = "✓",
                                    modifier = Modifier.color(palette.brand.primary).fontWeight(FontWeight.Bold)
                                        .margin(right = 8.px).flexShrink(0)
                                )
                                Div(
                                    attrs = Modifier.color(palette.custom.darkText).fontSize(18.px).lineHeight(1.3)
                                        .toAttrs { ref { el -> el.innerHTML = itemHtml; onDispose {} } })
                            }
                        }
                    }
                }
            }
            if (option.buttonText.isNotBlank() && option.linkPath.isNotBlank()) Link(
                path = option.linkPath, modifier = Modifier.fillMaxWidth().textDecorationLine(TextDecorationLine.None)
            ) {
                Box(
                    modifier = ActionButtonStyle.toModifier().fillMaxWidth().borderRadius(8.px)
                        .backgroundColor(palette.brand.primary).padding(topBottom = 12.px).cursor(Cursor.Pointer),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        text = option.buttonText,
                        modifier = Modifier.color(Colors.White).fontSize(20.px).fontWeight(FontWeight.Bold)
                    )
                }
            }
        }
    }
}

data class PatientPricingOption(
    val title: String,
    val badge: String,
    val targetProfile: String,
    val advantage: String,
    val inclusions: List<String>,
    val buttonText: String,
    val linkPath: String,
    val isFeatured: Boolean = false
)

data class PatientsPricingOptionsData(
    val sectionId: String? = null,
    val headline: String,
    val subtitle: String,
    val partnerBannerText: String,
    val options: List<PatientPricingOption>
)

private val globalPatientOptions = listOf(
    PatientPricingOption(
        title = "Learn the Science",
        badge = "CLINICAL EDUCATION",
        targetProfile = "Patients and caregivers exploring advanced neuroplasticity treatments.",
        advantage = "Understand how device-assisted therapy drives brain recovery so you can make informed decisions.",
        inclusions = listOf(
            "<b>Proven Efficacy:</b> Peer-reviewed clinical trial data",
            "<b>Clear Explanations:</b> How targeted movement rebuilds neural pathways",
            "<b>Patient Success:</b> Real recovery stories from clinical settings"
        ),
        buttonText = "Read the Evidence",
        linkPath = "/resources#resources-evidence",
        isFeatured = false
    ), PatientPricingOption(
        title = "Outpatient Clinics",
        badge = "CERTIFIED CARE",
        targetProfile = "Patients ready to begin scheduled therapy sessions with a certified professional.",
        advantage = "Receive direct, hands-on treatment using MyHand<sup>®</sup> at a <b>professional rehabilitation center</b>.",
        inclusions = listOf(
            "<b>Expert Supervision:</b> Direct oversight by licensed physical or occupational therapists",
            "<b>Integrated Care:</b> Combined seamlessly into your broader outpatient recovery plan",
            "<b>Safe Environment:</b> 100% clinically monitored and authorized sessions"
        ),
        buttonText = "Find a Partner Clinic",
        linkPath = "/contact",
        isFeatured = false
    ), PatientPricingOption(
        title = "Advocate for Access",
        badge = "PHYSICIAN REFERRAL",
        targetProfile = "Patients whose current physical therapist or hospital does not yet offer MyHand<sup>®</sup>.",
        advantage = "Empower your current healthcare provider to <b>adopt our technology</b> for your recovery.",
        inclusions = listOf(
            "<b>Provider Resources:</b> Access clinical documentation tailored for your doctor",
            "<b>Procurement Info:</b> Easy B2B adoption and leasing pathways for your clinic",
            "<b>Direct Connect:</b> Request our clinical sales team to reach out to your facility"
        ),
        buttonText = "Refer Your Clinic",
        linkPath = "/contact/#contact-patient-form-section",
        isFeatured = false
    )
)

// 2. Define the Region-Specific Regulatory Banners
private val usPatientData = PatientsPricingOptionsData(
    headline = "CLINICAL PATHWAYS TO RECOVERY",
    subtitle = "MyHand<sup>®</sup> is an FDA-cleared clinical device that requires direct, in-person supervision by a licensed healthcare professional.",
    partnerBannerText = "NOTE: For your safety and compliance with US FDA guidelines, MyHand<sup>®</sup> is exclusively available for use within a certified clinical setting.",
    options = globalPatientOptions
)

private val canadaPatientData = PatientsPricingOptionsData(
    headline = "CLINICAL PATHWAYS TO RECOVERY",
    subtitle = "MyHand<sup>®</sup> is a Health Canada approved clinical device requiring supervision by a licensed rehabilitation professional.",
    partnerBannerText = "NOTE: In accordance with Health Canada guidelines, MyHand<sup>®</sup> is administered exclusively through certified clinical partners.",
    options = globalPatientOptions
)

private val indiaPatientData = PatientsPricingOptionsData(
    headline = "CLINICAL PATHWAYS TO RECOVERY",
    subtitle = "MyHand<sup>®</sup> is a CDSCO approved clinical device designed for use in professional rehabilitation centers.",
    partnerBannerText = "NOTE: MyHand<sup>®</sup> therapy is administered exclusively by licensed clinical professionals.",
    options = globalPatientOptions
)

// 3. The Switchboard Getter
val defaultPatientsPricingData: PatientsPricingOptionsData
    get() = when (com.iregained.site.region.RegionConfig.currentRegion) {
        com.iregained.site.region.Region.US -> usPatientData
        com.iregained.site.region.Region.INDIA -> indiaPatientData
        com.iregained.site.region.Region.CANADA -> canadaPatientData
    }
